import React, { useCallback, useRef, useState } from "react";
import { Upload, X, Image as ImageIcon, GripVertical, Loader2, AlertCircle } from "lucide-react";
import imageCompression from "browser-image-compression";
import api from "@/api/client";
import { toast } from "sonner";

const ACCEPTED_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const MAX_FILES = 10;
const MAX_SIZE_MB = 8; // post-compression target

const compressionOptions = {
  maxSizeMB: 1.2,           // target compressed size
  maxWidthOrHeight: 1600,   // keep good quality for product images
  useWebWorker: true,
  initialQuality: 0.82,
  fileType: "image/jpeg",
};

/**
 * Modern image uploader.
 *  - Drag & drop OR pick from device (gallery / camera)
 *  - Multi-image (up to MAX_FILES)
 *  - Client-side compression (browser-image-compression)
 *  - Upload to /api/upload/product-image, save returned URL into `value`
 *  - Reorder by dragging, remove by clicking X
 *
 * Props:
 *   value:   string[]   – array of image URLs already saved
 *   onChange: (urls: string[]) => void
 *   maxFiles?: number (default 10)
 */
const ImageUploader = ({ value = [], onChange, maxFiles = MAX_FILES }) => {
  const [dragOver, setDragOver] = useState(false);
  const [uploads, setUploads] = useState([]); // [{id, name, progress, error}]
  const inputRef = useRef(null);
  const dragIndexRef = useRef(null);

  const validate = (file) => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      return `${file.name}: Unsupported format. Use JPG, PNG or WEBP.`;
    }
    if (file.size > 20 * 1024 * 1024) {
      return `${file.name}: File too large (max 20MB pre-compression).`;
    }
    return null;
  };

  const handleFiles = useCallback(async (fileList) => {
    const files = Array.from(fileList);
    const available = Math.max(0, maxFiles - value.length);
    if (available === 0) {
      toast.error(`Maximum ${maxFiles} images allowed`);
      return;
    }
    const toProcess = files.slice(0, available);
    if (files.length > available) {
      toast.warning(`Only ${available} more image(s) allowed. Extra files skipped.`);
    }

    // Initialize tracking
    const newUploads = toProcess.map((f) => ({ id: `${f.name}-${Date.now()}-${Math.random()}`, name: f.name, progress: 0, error: null }));
    setUploads((prev) => [...prev, ...newUploads]);

    const uploadedUrls = [];
    for (let i = 0; i < toProcess.length; i++) {
      const file = toProcess[i];
      const track = newUploads[i];
      const err = validate(file);
      if (err) {
        setUploads((prev) => prev.map((u) => (u.id === track.id ? { ...u, error: err, progress: 0 } : u)));
        toast.error(err);
        continue;
      }
      try {
        // Compress (skip if already tiny)
        let processed = file;
        if (file.size > 250 * 1024) {
          processed = await imageCompression(file, compressionOptions);
        }
        // FormData
        const fd = new FormData();
        const filename = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
        fd.append("file", processed, filename);

        const { data } = await api.post("/upload/product-image", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (evt) => {
            if (!evt.total) return;
            const pct = Math.round((evt.loaded / evt.total) * 100);
            setUploads((prev) => prev.map((u) => (u.id === track.id ? { ...u, progress: pct } : u)));
          },
        });
        const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
        uploadedUrls.push(fullUrl);
        setUploads((prev) => prev.map((u) => (u.id === track.id ? { ...u, progress: 100 } : u)));
      } catch (ex) {
        const msg = ex.response?.data?.detail || ex.message || "Upload failed";
        setUploads((prev) => prev.map((u) => (u.id === track.id ? { ...u, error: typeof msg === "string" ? msg : "Upload failed", progress: 0 } : u)));
        toast.error(`${file.name}: ${typeof msg === "string" ? msg : "Upload failed"}`);
      }
    }

    if (uploadedUrls.length > 0) {
      onChange([...value, ...uploadedUrls]);
      toast.success(`${uploadedUrls.length} image(s) uploaded`);
    }
    // Clear successful upload tracking after a moment
    setTimeout(() => {
      setUploads((prev) => prev.filter((u) => u.error));
    }, 1200);
  }, [value, onChange, maxFiles]);

  const onDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const onDragLeave = () => setDragOver(false);
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer?.files?.length) handleFiles(e.dataTransfer.files);
  };

  const remove = (idx) => {
    const next = value.filter((_, i) => i !== idx);
    onChange(next);
  };

  // Reorder via HTML5 DnD on previews
  const onDragStartItem = (i) => () => { dragIndexRef.current = i; };
  const onDragOverItem = (e) => e.preventDefault();
  const onDropItem = (i) => (e) => {
    e.preventDefault();
    const from = dragIndexRef.current;
    if (from == null || from === i) return;
    const next = [...value];
    const [m] = next.splice(from, 1);
    next.splice(i, 0, m);
    onChange(next);
    dragIndexRef.current = null;
  };

  return (
    <div className="space-y-3" data-testid="image-uploader">
      {/* Drop zone */}
      <div
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className={`cursor-pointer rounded-xl border-2 border-dashed transition-all p-5 sm:p-6 text-center ${
          dragOver ? "border-primary bg-primary/5" : "border-border bg-muted/30 hover:border-primary/60 hover:bg-muted/50"
        }`}
        data-testid="upload-dropzone"
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          accept="image/jpeg,image/jpg,image/png,image/webp"
          className="hidden"
          onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
          data-testid="upload-file-input"
        />
        <div className="flex flex-col items-center gap-2">
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <Upload className="w-6 h-6 text-primary" />
          </div>
          <div className="text-sm font-semibold text-foreground">Drag & drop images here</div>
          <div className="text-xs text-muted-foreground">or tap to choose from gallery / camera</div>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}
            className="mt-2 bg-primary hover:bg-primary-hover text-white font-semibold text-sm px-4 py-2 rounded-lg"
            data-testid="btn-choose-images"
          >
            Choose Images
          </button>
          <div className="text-[11px] text-muted-foreground mt-1">
            JPG, PNG, WEBP · up to {maxFiles} images · auto-compressed
          </div>
          <div className="text-[11px] text-primary font-semibold mt-1">
            Recommended: 4:5 portrait ratio (e.g. 1200×1500 px)
          </div>
        </div>
      </div>

      {/* Upload progress chips */}
      {uploads.length > 0 && (
        <div className="space-y-1.5" data-testid="upload-progress-list">
          {uploads.map((u) => (
            <div key={u.id} className="flex items-center gap-2 text-xs bg-muted/40 border border-border rounded-lg px-3 py-2">
              {u.error ? <AlertCircle className="w-4 h-4 text-destructive shrink-0" /> : <Loader2 className="w-4 h-4 text-primary animate-spin shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{u.name}</div>
                {u.error ? (
                  <div className="text-destructive truncate">{u.error}</div>
                ) : (
                  <div className="mt-1 h-1.5 bg-border rounded overflow-hidden">
                    <div className="h-full bg-primary transition-all" style={{ width: `${u.progress}%` }} />
                  </div>
                )}
              </div>
              <span className="text-muted-foreground tabular-nums">{u.error ? "Failed" : `${u.progress}%`}</span>
            </div>
          ))}
        </div>
      )}

      {/* Previews */}
      {value.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-2" data-testid="image-previews">
          {value.map((url, i) => (
            <div
              key={`${url}-${i}`}
              draggable
              onDragStart={onDragStartItem(i)}
              onDragOver={onDragOverItem}
              onDrop={onDropItem(i)}
              className="relative group rounded-lg overflow-hidden border border-border bg-muted aspect-[4/5]"
              data-testid={`image-preview-${i}`}
            >
              <img src={url} alt={`product ${i + 1}`} className="w-full h-full object-cover" />
              {i === 0 && (
                <span className="absolute top-1 left-1 text-[9px] font-bold bg-primary text-white px-1.5 py-0.5 rounded uppercase">
                  Primary
                </span>
              )}
              <button
                type="button"
                onClick={() => remove(i)}
                className="absolute top-1 right-1 bg-black/60 hover:bg-destructive text-white rounded-full w-6 h-6 flex items-center justify-center opacity-90 group-hover:opacity-100 transition"
                aria-label="Remove image"
                data-testid={`btn-remove-image-${i}`}
              >
                <X className="w-3.5 h-3.5" />
              </button>
              <div className="absolute bottom-1 left-1 bg-black/40 text-white rounded p-0.5 opacity-70 group-hover:opacity-100 cursor-grab">
                <GripVertical className="w-3 h-3" />
              </div>
            </div>
          ))}
        </div>
      )}

      {value.length === 0 && uploads.length === 0 && (
        <div className="text-[11px] text-muted-foreground flex items-center gap-1.5">
          <ImageIcon className="w-3.5 h-3.5" /> No images yet. Add at least one to publish.
        </div>
      )}
    </div>
  );
};

export default ImageUploader;
